import { sortLocationsByMarketplacePriority } from '../location/sort-locations-by-marketplace-priority';

export interface JobUrlInput {
  normalisedTitle?: string | null;
  title?: string | null;
  location?: string | string[] | null;
  remoteness?: string | null;
  companyId?: string | null;
  companyName?: string | null;
}

/**
 * Picks the best title to use as the SEO slug component of a job URL.
 *
 * Prefers `normalisedTitle` (cleaner, deduplicated), but falls back to `title`
 * whenever `normalisedTitle` is missing OR an empty/whitespace-only string.
 * Using nullish coalescing (`??`) here would be wrong: it short-circuits on
 * `''`, which is exactly the value Algolia returns for jobs whose only
 * indexed locale variant is for a different language. That bug produced
 * slug-less URLs like `/fi/tyopaikka/{jobId}/{companyId}` that crawlers
 * indexed.
 */
export function resolveJobUrlTitle(input: Pick<JobUrlInput, 'normalisedTitle' | 'title'>): string {
  const normalised =
    typeof input.normalisedTitle === 'string' ? input.normalisedTitle.trim() : '';
  if (normalised) return normalised;
  const title = typeof input.title === 'string' ? input.title.trim() : '';
  return title;
}

/**
 * Picks the location to embed in a job's URL. When a job lists multiple locations
 * (e.g. a multi-country remote posting), prefers the Finland-relevant one so this
 * Finnish job board's URLs/slugs read as local rather than defaulting to whichever
 * country happened to be listed first by the source ATS.
 */
export function resolveJobUrlLocation(input: Pick<JobUrlInput, 'location'>): string {
  const raw = input.location;
  if (Array.isArray(raw)) {
    const values = raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
    const [primary] = sortLocationsByMarketplacePriority(values);
    return String(primary ?? '').trim();
  }
  return String(raw ?? '').trim();
}

export function resolveJobUrlRemoteness(input: Pick<JobUrlInput, 'remoteness'>): string {
  return String(input.remoteness ?? '').trim();
}

export function resolveJobUrlCompany(
  input: Pick<JobUrlInput, 'companyId' | 'companyName'>,
  fallback: string = '',
): string {
  const companyId = typeof input.companyId === 'string' ? input.companyId.trim() : '';
  if (companyId) return companyId;

  const companyName = typeof input.companyName === 'string' ? input.companyName.trim() : '';
  if (companyName) return companyName;

  return String(fallback).trim();
}
